import React, { useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Save, Loader2, KeyRound, User, Stethoscope } from "lucide-react";

export default function AdminSettings() {
  const { user } = useAuth();
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [diag, setDiag] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);

  const runDiagnostics = async () => {
    setDiagBusy(true);
    setDiag(null);
    try {
      const { data } = await api.get("/diagnostics");
      setDiag(data);
    } catch (e) {
      setDiag({ error: e?.response?.data?.detail || e.message });
    } finally {
      setDiagBusy(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (newPw.length < 8) {
      setMsg({ kind: "error", text: "New password must be at least 8 characters." });
      return;
    }
    if (newPw !== confirmPw) {
      setMsg({ kind: "error", text: "New password and confirmation do not match." });
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPw,
        new_password: newPw,
      });
      setMsg({ kind: "success", text: "Password updated. You will stay signed in for now." });
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
    } catch (e) {
      setMsg({ kind: "error", text: e?.response?.data?.detail || "Failed to update password." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminLayout>
      <div className="mb-8">
        <div className="hed-overline">Account</div>
        <h1 className="font-serif text-3xl sm:text-4xl mt-2 tracking-tight">Settings</h1>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 max-w-4xl">
        {/* Profile card */}
        <div className="hed-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <User size={16} strokeWidth={2} className="text-[#5E6033]" />
            <div className="hed-overline">Your profile</div>
          </div>
          <div className="space-y-3">
            <div>
              <div className="text-[11px] text-[#5C5C54]">Email</div>
              <div className="text-sm font-semibold">{user?.email}</div>
            </div>
            <div>
              <div className="text-[11px] text-[#5C5C54]">Role</div>
              <div className="text-sm font-semibold capitalize">{user?.role || "staff"}</div>
            </div>
          </div>
        </div>

        {/* Change password card */}
        <div className="hed-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <KeyRound size={16} strokeWidth={2} className="text-[#5E6033]" />
            <div className="hed-overline">Change password</div>
          </div>
          <form onSubmit={onSubmit} className="space-y-4">
            <Field label="Current password">
              <input
                type="password"
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                required
                data-testid="current-password-input"
                className="w-full bg-transparent border-b border-[#E8E6DF] focus:border-[#B5953B] focus:outline-none py-2 text-sm"
              />
            </Field>
            <Field label="New password (min 8 chars)">
              <input
                type="password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                required
                data-testid="new-password-input"
                className="w-full bg-transparent border-b border-[#E8E6DF] focus:border-[#B5953B] focus:outline-none py-2 text-sm"
              />
            </Field>
            <Field label="Confirm new password">
              <input
                type="password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                required
                data-testid="confirm-password-input"
                className="w-full bg-transparent border-b border-[#E8E6DF] focus:border-[#B5953B] focus:outline-none py-2 text-sm"
              />
            </Field>

            {msg && (
              <div
                data-testid="password-message"
                className={`text-xs border-l-2 pl-3 py-1 ${
                  msg.kind === "success"
                    ? "text-[#3A5A40] border-[#3A5A40]"
                    : "text-[#9E2A2B] border-[#9E2A2B]"
                }`}
              >
                {msg.text}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="change-password-button"
              className="inline-flex items-center gap-2 bg-[#1A1A18] hover:bg-[#2a2a26] disabled:opacity-60 text-white px-5 py-2.5 rounded-sm text-sm font-semibold transition-colors"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} strokeWidth={2} />}
              Update password
            </button>
          </form>
        </div>
      </div>

      {/* System diagnostics (owner-only) */}
      {user?.role === "owner" && (
        <div className="hed-card p-6 mt-6 max-w-4xl">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Stethoscope size={16} strokeWidth={2} className="text-[#5E6033]" />
              <div className="hed-overline">System diagnostics</div>
            </div>
            <button
              onClick={runDiagnostics}
              disabled={diagBusy}
              data-testid="run-diagnostics-button"
              className="inline-flex items-center gap-2 bg-[#1A1A18] hover:bg-[#2a2a26] disabled:opacity-60 text-white px-4 py-2 rounded-sm text-sm font-semibold transition-colors"
            >
              {diagBusy ? <Loader2 size={14} className="animate-spin" /> : <Stethoscope size={14} strokeWidth={2} />}
              Run check
            </button>
          </div>
          <p className="text-xs text-[#5C5C54] mb-4">
            Verifies environment variables and live AI connectivity. If extraction is failing on
            production, run this here AND on your production URL — anything red is your problem.
          </p>
          {diag && (
            <pre
              data-testid="diagnostics-output"
              className="text-[11px] bg-[#FAFAF8] border border-[#E8E6DF] p-3 rounded-sm overflow-x-auto whitespace-pre-wrap"
            >
              {JSON.stringify(diag, null, 2)}
            </pre>
          )}
        </div>
      )}
    </AdminLayout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="hed-overline block mb-1" style={{ fontSize: 10 }}>{label}</label>
      {children}
    </div>
  );
}
